import React, { useState, useMemo, useCallback } from 'react';
import { useTruncatedText } from '../useTruncatedText';
import '../ChatCard.css';
import './CommunityAnnouncementCard.css';

const COLLAPSED_LINES = 6;
const MAX_VISIBLE_IMAGES = 4;

/**
 * Render body text with simple newline preservation.
 * Backend bodies sometimes contain "\n\n" paragraph breaks.
 */
const renderBody = (text) => {
  if (!text) return null;
  const parts = text.split('\n');
  return parts.map((line, idx) => (
    <React.Fragment key={idx}>
      {line}
      {idx < parts.length - 1 && <br />}
    </React.Fragment>
  ));
};

/**
 * Render footer text with newline preservation (footers like
 * "Targets: Kiln Boulder\nImage attached: Yes\n\nPlease reply..." need it).
 */
const renderFooter = (text) => {
  if (!text) return null;
  return text.split('\n').map((line, idx, arr) => (
    <React.Fragment key={idx}>
      {line}
      {idx < arr.length - 1 && <br />}
    </React.Fragment>
  ));
};

/**
 * Image gallery for a single announcement.
 * Layout adapts to count: 1 / 2 / 3 / 4 / 5+ (last tile shows "+N").
 * Returns null when there are no images — text-only announcements skip
 * the image block entirely and the content section sits flush with the
 * card's top rounded corners.
 *
 * Clicking any tile (including the +N overlay) invokes `onImageClick`
 * with the full image list and the clicked index, so the parent can
 * open the shared lightbox cycling through every image.
 */
const ImageGrid = ({ images, onImageLoad, alt, onImageClick }) => {
  // Track URLs that failed to load. When an image errors we DROP the whole
  // tile and let the grid re-flow (e.g. --3 → --2) instead of leaving a
  // dead/empty cell with the grid background showing through.
  const [failedUrls, setFailedUrls] = useState(() => new Set());

  const validImages = useMemo(
    () => (images || []).filter(url => !failedUrls.has(url)),
    [images, failedUrls]
  );

  const handleError = useCallback((url) => () => {
    setFailedUrls(prev => {
      if (prev.has(url)) return prev;
      const next = new Set(prev);
      next.add(url);
      return next;
    });
  }, []);

  if (!validImages || validImages.length === 0) return null;

  const visible = validImages.slice(0, MAX_VISIBLE_IMAGES);
  const hidden = Math.max(0, validImages.length - MAX_VISIBLE_IMAGES);

  const handleTileClick = (idx) => {
    if (typeof onImageClick === 'function') {
      onImageClick(validImages, idx);
    }
  };

  return (
    <div className={`ca-image-grid ca-image-grid--${visible.length}`}>
      {visible.map((url, idx) => {
        const isLastTile = idx === visible.length - 1 && hidden > 0;
        return (
          <div
            key={idx}
            className="ca-image-tile"
            onClick={() => handleTileClick(idx)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleTileClick(idx);
              }
            }}
            aria-label={`Open image ${idx + 1} of ${images.length}`}
          >
            <img
              src={url}
              alt={`${alt} (${idx + 1})`}
              onLoad={idx === 0 ? onImageLoad : undefined}
              onError={handleError(url)}
            />
            {isLastTile && (
              <div className="ca-image-more" aria-hidden="true">
                <span>+{hidden}</span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

/**
 * Community Announcement card. Two variants:
 *   - 'preview'  → user previewing their own draft (Approve / Rewrite / Cancel)
 *   - 'approval' → director reviewing a pending submission (Approve / Cancel)
 *
 * Props:
 *   - data: result of getCommunityAnnouncementData()
 *   - onImageLoad: callback when first image loads (for chat auto-scroll)
 *
 * The header is NOT rendered inside the card — the typewriter above the
 * card displays it (matching how event cards work).
 */
const CommunityAnnouncementCard = ({ data, onImageLoad, onImageClick }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { ref: bodyRef, truncatedText, isLong } = useTruncatedText(data?.body, COLLAPSED_LINES);

  if (!data) return null;

  const variantClass = data.variant === 'approval' ? 'ca-card--approval' : 'ca-card--preview';
  const visibleBody = isExpanded ? data.body : truncatedText;

  return (
    <div className={`ca-card ${variantClass}`}>
      <ImageGrid
        images={data.images}
        onImageLoad={onImageLoad}
        onImageClick={onImageClick}
        alt="Announcement image"
      />

      <div className="ca-card-content">
        {data.body && (
          <div
            ref={bodyRef}
            className={`ca-card-body${isExpanded ? ' is-expanded' : ''}`}
          >
            {renderBody(visibleBody)}
            {isLong && (
              <>
                {' '}
                <button
                  type="button"
                  className="ca-card-toggle"
                  onClick={() => setIsExpanded(v => !v)}
                  aria-expanded={isExpanded}
                  aria-label={isExpanded ? 'Hide announcement details' : 'Show full announcement'}
                >
                  {isExpanded ? 'Hide details' : 'Show more'}
                </button>
              </>
            )}
          </div>
        )}

        {data.footer && (
          <div className="ca-card-footer">
            {renderFooter(data.footer)}
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(CommunityAnnouncementCard);
